import React, { useEffect, useState, useCallback } from 'react';
import { getCase, updateCase } from './dprApi';
import CaseWizard, { STEP_GROUPS, ALL_STEPS } from './CaseWizard';
import CmaWizard, { CMA_STEP_GROUPS, CMA_ALL_STEPS } from './CmaWizard';
import CaseReport from './CaseReport';
import NarrativeReport from './NarrativeReport';
import { Badge } from './dprFields';

// Whether a given wizard step actually has data filled in — used instead of
// a blanket "computed_schedules exists so every step must be done" check,
// which false-positives every time a step is added to the wizard after a
// case was already completed once (the old check would green-tick brand-new
// steps nobody has ever opened).
function stepHasData(caseData, stepId, isCma) {
  const bp = caseData.business_profile || {};
  const cop = caseData.cost_of_project || {};
  const mof = caseData.means_of_finance || {};
  const cap = caseData.capacity_config || {};
  const rm = caseData.raw_material || {};
  const labour = caseData.labour_config || {};
  const power = caseData.power_config || {};
  const indirect = caseData.indirect_expenses || {};
  const salesCfg = caseData.sales_config || {};
  const fa = caseData.fixed_assets || {};
  const df = caseData.direct_financials || {};

  if (isCma) {
    switch (stepId) {
      case 'company': return !!caseData.applicant_name;
      case 'years': return (caseData.years_setup?.years || []).length > 0;
      case 'os': return Object.values(df.os || {}).some(col => (col || []).some(v => v));
      case 'bs': return Object.values(df.bs || {}).some(col => (col || []).some(v => v));
      case 'term_loans': return (caseData.term_loans || []).length > 0;
      case 'wc_method': return !!caseData.wc_config?.method;
      default: return false;
    }
  }
  switch (stepId) {
    case 'profile': return !!(caseData.applicant_name && caseData.project_title);
    case 'promoters': return (bp.promoters || []).some(p => p.name);
    case 'land_building': return (cop.landItems || []).length > 0 || (cop.buildingItems || []).length > 0;
    case 'plant_machinery': return (cop.plantMachineryItems || []).length > 0;
    case 'other_assets': return (cop.otherAssetItems || []).length > 0;
    case 'preoperative': return (cop.preopItems || []).length > 0 || (cop.securityDepositItems || []).length > 0 || !!cop.idcp || !!cop.preop;
    case 'subsidy': return !!caseData.subsidy_config?.schemeName;
    case 'raw_material': return !!cap.installedCapacityPerHour || (rm.recipeLines || []).length > 0;
    case 'man_power': return (labour.factory || []).length > 0 || (labour.admin || []).length > 0;
    case 'direct_expenses': return !!salesCfg.packagingCostPerUnit || (power.customExpenses || []).length > 0;
    case 'indirect_expenses': return (indirect.customLines || []).length > 0;
    case 'sources_of_finance': return (mof.equitySources || []).length > 0 || !!mof.promoterContribution;
    case 'term_loan': return !!caseData.term_loan_config?.amount;
    case 'depreciation': return (fa.blocks || []).length > 0;
    case 'working_capital': return !!caseData.wc_config?.method;
    default: return false;
  }
}

// Renders only the main content pane — the step rail lives in the shared
// DprSidebar (see DPRWorkspace.js), fed via the registerNav callback below
// so Dashboard and every open case share one persistent sidebar rather than
// each page owning its own.
export default function CaseDetail({ caseId, onBack, registerNav }) {
  const [caseData, setCaseData] = useState(null);
  const [stepId, setStepIdRaw] = useState(null);
  const [view, setView] = useState('wizard'); // wizard | report | narrative
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  // Explicit "which steps has the user actually submitted" tracking — a set
  // of step ids persisted in business_profile.completedStepIds (reused free-
  // form JSONB field, no migration needed), NOT a numeric "furthest index".
  // A numeric index breaks the moment a step is inserted anywhere but the
  // very end of the wizard (exactly what happened when Promoters/Indirect
  // Expenses/Sources of Finance were added mid-sequence — old cases' index
  // no longer lined up with the same conceptual step). An id-based set is
  // immune to step-list reordering.
  const [completedStepIds, setCompletedStepIds] = useState([]);

  const isCma = caseData?.case_mode === 'cma_only';
  const STEP_GROUPS_ACTIVE = isCma ? CMA_STEP_GROUPS : STEP_GROUPS;
  const ALL_STEPS_ACTIVE = isCma ? CMA_ALL_STEPS : ALL_STEPS;
  const WizardComponent = isCma ? CmaWizard : CaseWizard;

  // Tracks the last snapshot actually written to the server — every save
  // path (autosave, Save button, Next) updates this the moment it succeeds,
  // so the autosave effect below can tell "user typed something new" apart
  // from "caseData just changed because a save's own response came back."
  const lastPersistedRef = React.useRef(null);
  const autosaveTimerRef = React.useRef(null);

  const load = useCallback(() => {
    getCase(caseId).then(d => {
      lastPersistedRef.current = JSON.stringify(d);
      setCaseData(d);
      const isCmaCase = d.case_mode === 'cma_only';
      const steps = isCmaCase ? CMA_ALL_STEPS : ALL_STEPS;
      setStepIdRaw(steps[0].id);
      // Merge whatever was explicitly persisted with a one-time data-based
      // seed (for cases saved before this tracking existed) — never trust
      // "case was completed once" to imply a step added since is done.
      const stored = (d.business_profile || {}).completedStepIds || [];
      const seeded = steps.filter(s => stepHasData(d, s.id, isCmaCase)).map(s => s.id);
      setCompletedStepIds([...new Set([...stored, ...seeded])]);
    }).catch(e => setError(e.message));
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const persist = async (dataOverride) => {
    setSaving(true); setError(null);
    try {
      const updated = await updateCase(caseId, dataOverride || caseData);
      lastPersistedRef.current = JSON.stringify(updated);
      setCaseData(updated);
      setSavedAt(new Date());
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Autosave — every field edit lands on the server ~1.2s after the CA
  // stops typing, no explicit Save click needed. Skipped while just viewing
  // the Report/Narrative (nothing to save there) and whenever caseData's
  // change was actually a save's own response echoing back (compared via
  // lastPersistedRef, not a "just saved" flag, so it can't race a real edit
  // that happens to land in the same tick).
  useEffect(() => {
    if (!caseData || view !== 'wizard') return undefined;
    if (JSON.stringify(caseData) === lastPersistedRef.current) return undefined;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => { persist(); }, 1200);
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData, view]);

  const stepIndex = caseData && stepId ? ALL_STEPS_ACTIVE.findIndex(s => s.id === stepId) : -1;
  const isLastStep = stepIndex === ALL_STEPS_ACTIVE.length - 1;

  const setStepId = (id) => { setStepIdRaw(id); setView('wizard'); };

  // "Save" persists the current step's data in place — no navigation, no
  // marking the step complete — for a CA who wants to save mid-entry without
  // being forced onward. "Next" (handleNext) does that plus advances, which
  // is what actually marks the step done.
  const handleSave = async () => { await persist(); };

  const handleNext = async () => {
    const nextCompleted = [...new Set([...completedStepIds, stepId])];
    const withProgress = { ...caseData, business_profile: { ...(caseData.business_profile || {}), completedStepIds: nextCompleted } };
    setCaseData(withProgress);
    const ok = await persist(withProgress);
    if (!ok) return;
    setCompletedStepIds(nextCompleted);
    if (isLastStep) {
      setView('report');
    } else {
      setStepIdRaw(ALL_STEPS_ACTIVE[stepIndex + 1].id);
    }
  };
  const handleBack = () => {
    const prev = ALL_STEPS_ACTIVE[stepIndex - 1];
    if (prev) setStepIdRaw(prev.id);
  };

  const allStepsComplete = ALL_STEPS_ACTIVE.every(s => completedStepIds.includes(s.id));

  // Feed the current nav state up to the shared sidebar every render — cheap,
  // and keeps DprSidebar a pure display component with no case-mode logic.
  useEffect(() => {
    if (!registerNav) return;
    if (!caseData || !stepId) { registerNav(null); return; }
    registerNav({
      title: caseData.project_title, isCma, stepGroups: STEP_GROUPS_ACTIVE, allSteps: ALL_STEPS_ACTIVE,
      stepId, view, completedStepIds, allStepsComplete,
      onStepClick: setStepId, onViewReport: () => setView('report'), onViewNarrative: () => setView('narrative'),
    });
    return () => registerNav(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData, stepId, view, completedStepIds, allStepsComplete, isCma]);

  if (!caseData || !stepId) return <div className="p-10 text-sm text-slate-400">Loading…</div>;

  return (
    <div className="flex-1 min-w-0">
      <div className="sticky top-0 z-10 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-b border-slate-200 dark:border-slate-800 px-6 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onBack}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-bold text-[#001254] dark:text-slate-200 hover:border-[#ff5200]/40 hover:text-[#ff5200] hover:bg-orange-500/5 transition-colors flex-shrink-0">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
            All Projects
          </button>
          <Badge tone="indigo">{view === 'report' ? 'Project Report' : view === 'narrative' ? 'Narrative DPR' : `Step ${stepIndex + 1} of ${ALL_STEPS_ACTIVE.length}`}</Badge>
          {isCma && <Badge tone="slate">CMA Only</Badge>}
          <span className="text-sm font-extrabold text-[#001254] dark:text-slate-100 truncate">{caseData.applicant_name}</span>
        </div>
        <div className="flex items-center gap-3">
          {saving && <span className="text-xs text-slate-400">Saving…</span>}
          {savedAt && !saving && <span className="text-xs text-emerald-500">Saved {savedAt.toLocaleTimeString()}</span>}
          {error && <span className="text-xs text-rose-500">{error}</span>}
        </div>
      </div>

      <div className="p-6">
        {view === 'wizard' && (
          <WizardComponent caseData={caseData} setCaseData={setCaseData} stepId={stepId}
            onNext={handleNext} onBack={handleBack} onSave={handleSave} saving={saving} isLastStep={isLastStep} />
        )}
        {view === 'report' && <CaseReport caseData={caseData} schedules={caseData.computed_schedules} mode={caseData.case_mode} />}
        {view === 'narrative' && !isCma && <NarrativeReport caseId={caseId} caseData={caseData} setCaseData={setCaseData} />}
      </div>
    </div>
  );
}
